import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/UI/Button';
import type { Form, FormField } from '../../types/form';
import { isDisplayOnlyFieldType, resolveFormTheme } from '../../types/form';
import { FormFieldRenderer } from './FormFieldRenderer';
import { ensureFormThemeFontsLoaded } from './FormThemePreview';
import { getSectionFields, resolveNextSection, sortSections } from './formNavigation';

export interface FormPreviewFormProps {
  form: Form;
  onScrollTop?: () => void;
}

export const FormPreviewForm: React.FC<FormPreviewFormProps> = ({
  form,
  onScrollTop,
}) => {
  const theme = useMemo(
    () => resolveFormTheme(form.theme),
    [form.theme]
  );

  const [answers, setAnswers] = useState<Record<string, string>>(() =>
  {
    const initialAnswers: Record<string, string> = {};
    form.fields.forEach((field) =>
    {
      initialAnswers[field.id] = '';
    });
    return initialAnswers;
  });
  const [files, setFiles] = useState<Record<string, File | null>>(() =>
  {
    const initialFiles: Record<string, File | null> = {};
    form.fields.forEach((field) =>
    {
      if (field.type === 'FileUpload')
      {
        initialFiles[field.id] = null;
      }
    });
    return initialFiles;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(
    () => sortSections(form.sections)[0]?.id ?? null
  );
  const [sectionHistory, setSectionHistory] = useState<string[]>([]);
  const [previewNotice, setPreviewNotice] = useState<string | null>(null);

  useEffect(() => {
    ensureFormThemeFontsLoaded();
  }, []);

  const validateFields = (fieldsToValidate: FormField[]): boolean => {
    const nextErrors: Record<string, string> = {};

    fieldsToValidate.forEach((field) =>
    {
      if (isDisplayOnlyFieldType(field.type))
      {
        return;
      }

      if (field.type === 'FileUpload')
      {
        const file = files[field.id];
        if (field.isRequired && !file)
        {
          nextErrors[field.id] = 'Esta pergunta é obrigatória.';
        }
        return;
      }

      const value = (answers[field.id] ?? '').trim();

      if (field.isRequired && !value)
      {
        nextErrors[field.id] = 'Esta pergunta é obrigatória.';
        return;
      }

      if (value && field.validationRegex)
      {
        try
        {
          const regex = new RegExp(field.validationRegex);
          if (!regex.test(value))
          {
            nextErrors[field.id] =
              field.validationMessage || 'Resposta inválida.';
          }
        }
        catch
        {
          // Regex inválida no builder — ignora na prévia
        }
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const currentSectionFields = (): FormField[] => {
    if (!currentSectionId)
    {
      return form.fields;
    }
    return getSectionFields(form.fields, currentSectionId);
  };

  const showPreviewBlockedNotice = () => {
    setPreviewNotice(
      'Esta é apenas uma visualização. As respostas não serão enviadas.'
    );
  };

  const scrollToTop = () => {
    if (onScrollTop)
    {
      onScrollTop();
      return;
    }

    if (typeof window !== 'undefined')
    {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoNext = () => {
    if (!currentSectionId)
    {
      return;
    }

    if (!validateFields(currentSectionFields()))
    {
      return;
    }

    const next = resolveNextSection(form, currentSectionId, answers);
    if (next.kind === 'submit')
    {
      showPreviewBlockedNotice();
      return;
    }

    setPreviewNotice(null);
    setSectionHistory((prev) => [...prev, currentSectionId]);
    setCurrentSectionId(next.sectionId);
    setErrors({});
    scrollToTop();
  };

  const handleGoBack = () => {
    setPreviewNotice(null);
    setSectionHistory((prev) =>
    {
      if (prev.length === 0)
      {
        return prev;
      }
      const history = [...prev];
      const previousSectionId = history.pop() as string;
      setCurrentSectionId(previousSectionId);
      setErrors({});
      return history;
    });
    scrollToTop();
  };

  const resetAnswers = () => {
    const resetAnswersMap: Record<string, string> = {};
    const resetFiles: Record<string, File | null> = {};
    form.fields.forEach((field) =>
    {
      resetAnswersMap[field.id] = '';
      if (field.type === 'FileUpload')
      {
        resetFiles[field.id] = null;
      }
    });
    setAnswers(resetAnswersMap);
    setFiles(resetFiles);
    setErrors({});
    setEmail('');
    setPreviewNotice(null);
    setCurrentSectionId(sortSections(form.sections)[0]?.id ?? null);
    setSectionHistory([]);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleGoNext();
  };

  const sortedSections = sortSections(form.sections);
  const hasMultipleSections = sortedSections.length > 1;
  const activeSectionId = currentSectionId ?? sortedSections[0]?.id ?? null;
  const activeSection = sortedSections.find((s) => s.id === activeSectionId);
  const activeSectionIndex = sortedSections.findIndex(
    (s) => s.id === activeSectionId
  );
  const visibleFields = activeSectionId
    ? getSectionFields(form.fields, activeSectionId)
    : form.fields.slice().sort((a, b) => a.order - b.order);

  const hasRequiredFields = visibleFields.some((field) => field.isRequired);
  const nextStep = activeSectionId
    ? resolveNextSection(form, activeSectionId, answers)
    : { kind: 'submit' as const };
  const isLastStep = nextStep.kind === 'submit';
  const canGoBack = sectionHistory.length > 0;
  const isFirstSectionCard = !hasMultipleSections || activeSectionIndex === 0;

  return (
    <form onSubmit={handleFormSubmit} className="mx-auto w-full max-w-4xl" noValidate>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {theme.headerImageUrl && isFirstSectionCard ? (
          <img
            src={theme.headerImageUrl}
            alt=""
            className="h-44 w-full object-cover"
          />
        ) : null}

        {isFirstSectionCard && (
          <div className="space-y-2 border-b border-gray-100 px-6 py-5">
            <div
              className="mb-2 h-1.5 w-14 rounded-full"
              style={{ backgroundColor: theme.primaryColor }}
            />
            <h1
              className="font-semibold text-gray-900"
              style={{ fontSize: theme.textSizes.title }}
            >
              {form.title || 'Formulário sem título'}
            </h1>
            {form.description && (
              <p
                className="text-gray-600"
                style={{ fontSize: theme.textSizes.body }}
              >
                {form.description}
              </p>
            )}
            {hasRequiredFields && (
              <p
                className="text-gray-500"
                style={{ fontSize: theme.textSizes.body }}
              >
                Campos marcados com{' '}
                <span className="font-medium text-gray-700">obrigatório</span>{' '}
                precisam ser preenchidos.
              </p>
            )}
          </div>
        )}

        {hasMultipleSections && (
          <div className="border-b border-gray-100 px-6 py-4">
            <p
              className="text-xs font-medium uppercase tracking-wide"
              style={{ color: theme.primaryColor }}
            >
              Seção {activeSectionIndex + 1} de {sortedSections.length}
            </p>
            {activeSection?.title && (
              <h2
                className="mt-1 font-semibold text-gray-900"
                style={{ fontSize: theme.textSizes.section }}
              >
                {activeSection.title}
              </h2>
            )}
            {activeSection?.description && (
              <p
                className="mt-1 text-gray-600"
                style={{ fontSize: theme.textSizes.body }}
              >
                {activeSection.description}
              </p>
            )}
          </div>
        )}

        <div className="space-y-6 px-6 py-6">
          {isFirstSectionCard && (
            <div>
              <label
                className="mb-1.5 block font-medium text-gray-800"
                style={{ fontSize: theme.textSizes.question }}
              >
                E-mail (opcional)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1"
                style={{
                  fontSize: theme.textSizes.body,
                  ['--tw-ring-color' as string]: theme.primaryColor,
                }}
                onFocus={(e) =>
                {
                  e.currentTarget.style.borderColor = theme.primaryColor;
                  e.currentTarget.style.boxShadow = `0 0 0 1px ${theme.primaryColor}`;
                }}
                onBlur={(e) =>
                {
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.boxShadow = '';
                }}
                placeholder="seu@email.com"
              />
            </div>
          )}

          {visibleFields.map((field) => (
            <div
              key={field.id}
              className="border-t border-gray-100 pt-6 first:border-t-0 first:pt-0"
              style={{ fontSize: theme.textSizes.question }}
            >
              <FormFieldRenderer
                field={field}
                value={answers[field.id] ?? ''}
                error={errors[field.id]}
                onChange={(value) =>
                {
                  setAnswers((prev) => ({ ...prev, [field.id]: value }));
                  if (errors[field.id])
                  {
                    setErrors((prev) =>
                    {
                      const next = { ...prev };
                      delete next[field.id];
                      return next;
                    });
                  }
                }}
                onFileChange={
                  field.type === 'FileUpload'
                    ? (file) =>
                      {
                        setFiles((prev) => ({ ...prev, [field.id]: file }));
                        if (errors[field.id])
                        {
                          setErrors((prev) =>
                          {
                            const next = { ...prev };
                            delete next[field.id];
                            return next;
                          });
                        }
                      }
                    : undefined
                }
              />
            </div>
          ))}
        </div>

        {previewNotice && (
          <div className="border-t border-amber-100 bg-amber-50 px-6 py-3">
            <p className="text-sm text-amber-900">{previewNotice}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-4 border-t border-gray-100 bg-gray-50 px-6 py-4">
          <div className="flex items-center gap-2">
            {canGoBack && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleGoBack}
              >
                Voltar
              </Button>
            )}
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: theme.primaryColor }}
            >
              {isLastStep ? 'Enviar' : 'Próxima'}
            </button>
          </div>
          <button
            type="button"
            className="text-sm text-gray-600 hover:text-gray-900"
            onClick={resetAnswers}
          >
            Limpar formulário
          </button>
        </div>
      </div>
    </form>
  );
};

export default FormPreviewForm;
